import { AbortController as _abortControllerPolyfill } from 'abortcontroller-polyfill/dist/cjs-ponyfill'

import { DEFAULT_PER_PAGE } from '@aytm/app/stats/page/components/lib/constants'
import { DEFAULT_PER_PAGE_QA } from '@aytm/app/stats/page/comments/lib/constants'

import PageGlobals from '@aytm/app/stats/lib/page/page-globals'
import {
  approve,
  reject,
  toggleKudos,
  setCodebook,
  setSurveyCodebook,
  setTag,
  setResponseTag,
  setSurveyTag,
  setSurveyTags,
  runSentiments,
  runSurveySkipper,
  runSurveyTranslation
} from '@aytm/app/stats/page/comments/apiCalls'

const AbortController = window.AbortController || _abortControllerPolyfill

// const abortSignal = () => {
//   if (fetchTimer) {
//     clearTimeout(fetchTimer)
//     fetchTimer = null
//   }

//   if (null !== abortController) {
//     abortController.abort()
//     abortController = null
//   }
// }

export const setPageData = ({ dispatch, pageData = [], extra = {} }) => {
  dispatch({
    type: 'SET_PARAMS',
    request: {
      value: {
        pageData,
        ...extra
      }
    }
  })
}

export const setQAMode = ({
  dispatch,
  pageSize,
  currentPage,
  totalRespondents,
  isQAMode,
  onQAModeChange,
  fetchDataDelay
}) => {
  setPageData({ dispatch, pageData: [], extra: { qaCounter: null } })

  const defaultPageSize = isQAMode ? DEFAULT_PER_PAGE_QA : DEFAULT_PER_PAGE

  if (1 == currentPage && pageSize == defaultPageSize) {
    fetchDataDelay()
  } else {
    dispatch({
      type: 'SET_REQUEST_PARAM_SET',
      request: {
        pageSize: defaultPageSize,
        currentPage: 1
      }
    })
  }

  if (isQAMode) {
    window.localStorage.setItem(`totalRespondentsCounter${PageGlobals.SURVEY_ID}`, totalRespondents)
  }

  if (onQAModeChange && 'function' == typeof onQAModeChange) {
    onQAModeChange(isQAMode)
  }
}

export const updateQACounter = ({ dispatch, qaCounter, qaTotal, uidsToApprove = null }) => {
  const amount = uidsToApprove ? uidsToApprove.reduce((cnt, item) => (!item.isRejected ? cnt + 1 : cnt), 0) : 1
  const totalRespondentsLocalStorage = parseInt(
    window.localStorage.getItem(`totalRespondentsCounter${PageGlobals.SURVEY_ID}`),
    10
  )
  const counter = null !== qaCounter ? parseInt(qaCounter, 10) + amount : amount

  if (counter <= totalRespondentsLocalStorage) {
    dispatch({
      type: 'SET_PARAMS',
      request: {
        value: {
          qaTotal: parseInt(qaTotal, 10) - amount > 0 ? parseInt(qaTotal, 10) - amount : 0,
          qaCounter: counter
        }
      }
    })

    window.localStorage.setItem(`qaCounter${PageGlobals.SURVEY_ID}`, counter.toString())
  }
}

export const changeResponseCommentStatus = async ({
  type,
  responseId,
  reason,
  scope,
  CSRF,
  commentStatusesState,
  dispatchCommentStatuses,
  onSuccess,
  onFetchApiKey
}) => {
  const abortController = new AbortController()

  const { signal } = abortController

  const prevStatus = commentStatusesState[scope] && commentStatusesState[scope][responseId]
  const data = 'reject' == type ? { scope, responseId, reason } : { scope, responseId }

  const errorCallback = async message => {
    await dispatchCommentStatuses({
      type: 'SET_STATUS',
      data: {
        ...data,
        status: { type: 'error', message: 'string' == typeof message ? message : 'Oops. Something went wrong.' }
      }
    })

    setTimeout(
      async () =>
        await dispatchCommentStatuses({
          type: prevStatus ? 'SET_STATUS' : 'CLEANUP',
          data: { ...data, status: prevStatus }
        }),
      3000
    )
  }

  await dispatchCommentStatuses({ type: 'SET_STATUS', data: { ...data, status: 'loading' } })

  try {
    const res =
      'reject' == type
        ? await reject({
            responseId,
            message: reason,
            authenticity_token: CSRF,
            signal
          })
        : 'approve' == type
          ? await approve({
              responseId,
              signal
            })
          : await toggleKudos({ responseId, type, authenticity_token: CSRF, signal })

    switch (res.status) {
      case 'OK': {
        await dispatchCommentStatuses({
          type: 'unkudos' == type ? 'CLEANUP' : 'SET_STATUS',
          data: 'unkudos' == type ? data : { ...data, status: 'reject' == type ? 'true' : 'revertable' }
        })
        if (onSuccess && 'function' == typeof onSuccess) {
          onSuccess()
        }
        break
      }
      case 'INVALID KEY': {
        if (onFetchApiKey && 'function' == typeof onFetchApiKey) {
          onFetchApiKey(() => {
            changeResponseCommentStatus({
              type,
              responseId,
              reason,
              scope,
              CSRF,
              commentStatusesState,
              dispatchCommentStatuses,
              onSuccess,
              onFetchApiKey
            })
          })
        } else errorCallback()
        break
      }
      case 'FAIL':
      default: {
        errorCallback(res.error)
        break
      }
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log(error)
    errorCallback()
  }
}

// mass assign codebook create/delete handler
export const changeCodebookState = async ({
  codebook,
  path = '*',
  request = {},
  responseId = '*',
  action = 'create',
  onFetch,
  onError = () => {},
  setIsLoading = () => {},
  onFetchApiKey,
  // each call keeps its own back-off, so calls running at once or one finishing meanwhile do not move it
  retryDelay = 1000
}) => {
  let fetchTimer = null

  const errorCallback = message => {
    setIsLoading(false, true)

    fetchTimer && clearTimeout(fetchTimer)

    onError({
      codebook: codebook.codebook,
      name: `${action}Error`,
      message: 'string' == typeof message ? message : 'Oops. Something went wrong.'
    })
  }

  try {
    const abortController = new AbortController()

    setIsLoading(true)

    const { signal } = abortController
    const res = await setCodebook({ codebook, path, request, responseId, action, signal })

    switch (res.status) {
      case 'OK': {
        fetchTimer && clearTimeout(fetchTimer)

        if (onFetch && 'function' == typeof onFetch) {
          onFetch(res, { codebook, path, request, responseId, action })
        }
        setIsLoading(false)
        break
      }
      case 'RUNNING': {
        fetchTimer = setTimeout(() => {
          changeCodebookState({
            codebook,
            path,
            request,
            responseId,
            action,
            onError,
            onFetch,
            setIsLoading,
            onFetchApiKey,
            retryDelay: retryDelay * 1.5
          })
        }, retryDelay)
        break
      }
      case 'INVALID KEY': {
        if (onFetchApiKey && 'function' == typeof onFetchApiKey) {
          onFetchApiKey(() => {
            changeCodebookState({
              codebook,
              path,
              request,
              responseId,
              action,
              onError,
              onFetch,
              setIsLoading,
              onFetchApiKey
            })
          })
        } else errorCallback()
        break
      }
      case 'FAIL':
      default: {
        errorCallback(res.error)
        break
      }
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log(error)
    errorCallback()
  }
}

// survey(schema) level codebook create/update/delete handler
export const changeSurveyCodebookState = async ({
  codebook,
  newValue = null,
  action = 'create',
  onFetch,
  onError = () => {},
  setIsLoading = () => {},
  onFetchApiKey,
  // each call keeps its own back-off, so calls running at once or one finishing meanwhile do not move it
  retryDelay = 1000
}) => {
  let fetchTimer = null

  const errorCallback = message => {
    setIsLoading(false, true)

    fetchTimer && clearTimeout(fetchTimer)

    onError({
      codebook: codebook.codebook,
      name: `${action}Error`,
      message: 'string' == typeof message ? message : 'Oops. Something went wrong.'
    })
  }

  try {
    const abortController = new AbortController()

    setIsLoading(true)

    const { signal } = abortController
    const res =
      'update' == action && newValue && codebook.codebook.trim() == newValue.trim()
        ? {
            error: 'cannot rename codebook to itself',
            status: 'FAIL'
          }
        : await setSurveyCodebook({ codebook, newValue, action, signal })

    switch (res.status) {
      case 'OK': {
        fetchTimer && clearTimeout(fetchTimer)

        if (onFetch && 'function' == typeof onFetch) {
          onFetch(res, { codebook, newValue, action })
        }
        setIsLoading(false)
        break
      }
      case 'RUNNING': {
        fetchTimer = setTimeout(() => {
          changeSurveyCodebookState({
            codebook,
            action,
            newValue,
            onFetch,
            onError,
            setIsLoading,
            onFetchApiKey,
            retryDelay: retryDelay * 1.5
          })
        }, retryDelay)
        break
      }
      case 'INVALID KEY': {
        if (onFetchApiKey && 'function' == typeof onFetchApiKey) {
          onFetchApiKey(() => {
            changeSurveyCodebookState({
              codebook,
              action,
              newValue,
              onFetch,
              onError,
              setIsLoading,
              onFetchApiKey
            })
          })
        } else errorCallback()
        break
      }
      case 'FAIL':
      default: {
        errorCallback(res.error)
        break
      }
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log(error)
    errorCallback()
  }
}

// mass assign tag create/delete handler
export const changeTagState = async ({
  tag,
  path = '*',
  request = {},
  responseId = '*',
  action = 'create',
  onFetch,
  onError = () => {},
  setIsLoading = () => {},
  onFetchApiKey,
  // each call keeps its own back-off, so calls running at once or one finishing meanwhile do not move it
  retryDelay = 1000
}) => {
  let fetchTimer = null

  const errorCallback = message => {
    setIsLoading(false, true)

    fetchTimer && clearTimeout(fetchTimer)

    onError({
      tag: tag.tag,
      codebook: tag.codebook,
      name: `${action}Error`,
      message: 'string' == typeof message ? message : 'Oops. Something went wrong.'
    })
  }

  try {
    const abortController = new AbortController()

    setIsLoading(true)

    const { signal } = abortController
    const res = await setTag({ tag, path, request, responseId, action, signal })

    switch (res.status) {
      case 'OK': {
        fetchTimer && clearTimeout(fetchTimer)

        if (onFetch && 'function' == typeof onFetch) {
          onFetch(res, { tag, path, request, responseId, action })
        }
        setIsLoading(false)
        break
      }
      case 'RUNNING': {
        fetchTimer = setTimeout(() => {
          changeTagState({
            tag,
            path,
            request,
            responseId,
            action,
            onFetch,
            onError,
            setIsLoading,
            onFetchApiKey,
            retryDelay: retryDelay * 1.5
          })
        }, retryDelay)
        break
      }
      case 'INVALID KEY': {
        if (onFetchApiKey && 'function' == typeof onFetchApiKey) {
          onFetchApiKey(() => {
            changeTagState({
              tag,
              path,
              request,
              responseId,
              action,
              onFetch,
              onError,
              setIsLoading,
              onFetchApiKey
            })
          })
        } else errorCallback()
        break
      }
      case 'FAIL':
      default: {
        errorCallback(res.error)
        break
      }
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log(error)
    errorCallback()
  }
}

// one response tag create/delete handler
export const changeResponseTagState = async ({
  tag,
  type,
  path,
  request = {},
  responseId,
  action = 'create',
  setIsLoading = () => {},
  onFetchApiKey,
  onUpdateSchema,
  dispatch,
  dispatchCommentStatuses,
  // each card keeps its own pace: a module-wide timer would let another card's answer cancel this retry
  retryDelay = 1000,
  // a key the page has just issued and the service still refuses will not get better on another ask
  keyRetried = false
}) => {
  const errorCallback = message => {
    setIsLoading(false, true)

    dispatchCommentStatuses({
      type: 'SET_TAG_STATUS',
      data: {
        scope: 'sentiment' == tag.type ? 'sentiment' : 'tags',
        path,
        responseId,
        status: { type: 'error', message: 'string' == typeof message ? message : 'Oops. Something went wrong.' }
      }
    })
    setTimeout(
      () =>
        dispatchCommentStatuses({
          type: 'CLEANUP_TAG_STATUS',
          data: { scope: 'sentiment' == tag.type ? 'sentiment' : 'tags', responseId, path }
        }),
      3000
    )
  }

  try {
    const abortController = new AbortController()

    setIsLoading(true)

    const { signal } = abortController
    const res = await setResponseTag({ tag, path, responseId, action, signal })

    switch (res.status) {
      case 'OK': {
        type && dispatch({ type, data: { tag, path, responseId, action } })

        if (onUpdateSchema && 'function' == typeof onUpdateSchema) {
          onUpdateSchema(res, { tag, path, request, responseId, action })
        }
        setIsLoading(false)
        break
      }
      case 'RUNNING': {
        setTimeout(
          () =>
            changeResponseTagState({
              tag,
              type,
              path,
              request,
              responseId,
              action,
              setIsLoading,
              onFetchApiKey,
              onUpdateSchema,
              dispatch,
              dispatchCommentStatuses,
              retryDelay: retryDelay * 1.5,
              keyRetried
            }),
          retryDelay
        )
        break
      }
      case 'INVALID KEY': {
        if (!keyRetried && onFetchApiKey && 'function' == typeof onFetchApiKey) {
          onFetchApiKey(() => {
            changeResponseTagState({
              tag,
              type,
              path,
              request,
              responseId,
              action,
              setIsLoading,
              onFetchApiKey,
              onUpdateSchema,
              dispatch,
              dispatchCommentStatuses,
              keyRetried: true
            })
          })
        } else errorCallback()
        break
      }
      case 'FAIL':
      default: {
        errorCallback(res.error)
        break
      }
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log(error)
    errorCallback()
  }
}

// survey(schema) level tag create/update/delete handler
export const changeSurveyTagState = async ({
  tag,
  newValue = null,
  action = 'create',
  onFetch,
  onError = () => {},
  setIsLoading = () => {},
  onFetchApiKey,
  // each call keeps its own back-off, so calls running at once or one finishing meanwhile do not move it
  retryDelay = 1000
}) => {
  let fetchTimer = null

  const errorCallback = message => {
    setIsLoading(false, true)

    fetchTimer && clearTimeout(fetchTimer)

    onError({
      tag: tag.tag,
      codebook: tag.codebook,
      name: `${action}Error`,
      message: 'string' == typeof message ? message : 'Oops. Something went wrong.'
    })
  }

  try {
    const abortController = new AbortController()

    setIsLoading(true)

    const { signal } = abortController
    const res =
      'update' == action && newValue && tag.tag.trim() == newValue.trim()
        ? {
            error: 'cannot rename tag to itself',
            status: 'FAIL'
          }
        : await setSurveyTag({ tag, newValue, action, signal })

    switch (res.status) {
      case 'OK': {
        fetchTimer && clearTimeout(fetchTimer)

        if (onFetch && 'function' == typeof onFetch) {
          onFetch(res, { tag, newValue, action })
        }
        setIsLoading(false)
        break
      }
      case 'RUNNING': {
        fetchTimer = setTimeout(() => {
          changeSurveyTagState({
            tag,
            newValue,
            action,
            onFetch,
            onError,
            setIsLoading,
            onFetchApiKey,
            retryDelay: retryDelay * 1.5
          })
        }, retryDelay)
        break
      }
      case 'INVALID KEY': {
        if (onFetchApiKey && 'function' == typeof onFetchApiKey) {
          onFetchApiKey(() => {
            changeSurveyTagState({
              tag,
              newValue,
              action,
              onFetch,
              onError,
              setIsLoading,
              onFetchApiKey
            })
          })
        } else errorCallback()
        break
      }
      case 'FAIL':
      default: {
        errorCallback(res.error)
        break
      }
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log(error)
    errorCallback()
  }
}
export const changeSurveyTagsState = async ({
  tags,
  codebook,
  action = 'create',
  onFetch,
  onError = () => {},
  setIsLoading = () => {},
  onFetchApiKey,
  // each call keeps its own back-off, so calls running at once or one finishing meanwhile do not move it
  retryDelay = 1000
}) => {
  let fetchTimer = null

  const errorCallback = message => {
    setIsLoading(false, true)

    fetchTimer && clearTimeout(fetchTimer)

    onError({
      codebook: codebook.codebook,
      name: `${action}Error`,
      message: message || 'Oops. Something went wrong.'
    })
  }

  try {
    const abortController = new AbortController()

    setIsLoading(true)

    const { signal } = abortController
    const res = await setSurveyTags({
      tags,
      codebook,
      action,
      signal
    })

    switch (res.status) {
      case 'OK': {
        fetchTimer && clearTimeout(fetchTimer)

        if (onFetch && 'function' == typeof onFetch) {
          onFetch(res, {
            tags,
            codebook,
            action
          })
        }
        setIsLoading(false)
        break
      }
      case 'RUNNING': {
        fetchTimer = setTimeout(() => {
          changeSurveyTagsState({
            tags,
            codebook,
            action,
            onFetch,
            onError,
            setIsLoading,
            onFetchApiKey,
            retryDelay: retryDelay * 1.5
          })
        }, retryDelay)
        break
      }
      case 'INVALID KEY': {
        if (onFetchApiKey && 'function' == typeof onFetchApiKey) {
          onFetchApiKey(() => {
            changeSurveyTagsState({
              tags,
              codebook,
              action,
              onFetch,
              onError,
              setIsLoading,
              onFetchApiKey
            })
          })
        } else errorCallback()
        break
      }
      case 'FAIL':
      default: {
        errorCallback(res.error)
        break
      }
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log(error)
    errorCallback()
  }
}

export const generateSentiments = async ({
  job,
  codebook,
  onFetch,
  onError = () => {},
  onProgress = () => {},
  setIsLoading = () => {},
  onFetchApiKey
}) => {
  let fetchTimer = null

  const errorCallback = message => {
    setIsLoading(false)

    fetchTimer && clearTimeout(fetchTimer)

    onError(
      Array.isArray(message) && 0 < message.length
        ? message
        : ['Sorry, mate, I&apos;ve lost my bearings...', 'Let&apos;s give it another go in a bit.']
    )
  }

  try {
    const abortController = new AbortController()

    setIsLoading(true)

    const { signal } = abortController
    const res = await runSentiments({ job, signal })

    switch (res.status) {
      case 'OK':
      case 'NOT FOUND': {
        fetchTimer && clearTimeout(fetchTimer)

        onProgress(100)

        if (onFetch && 'function' == typeof onFetch) {
          const message = res.result?.data?.message || []

          onFetch(res.result?.data || res, () => {
            if (Object.hasOwn(res.result?.data || {}, 'survey') || Object.hasOwn(res || {}, 'survey'))
              setIsLoading(false, message)
            else {
              setIsLoading(false)

              onError(
                (Array.isArray(message) && 0 < message.length && message) || [
                  'Sorry, mate, I&apos;ve lost my bearings...',
                  'Let&apos;s give it another go in a bit.'
                ]
              )
            }
          })
        }
        break
      }
      case 'RUNNING': {
        if (res && Object.hasOwn(res, 'token')) {
          onProgress(res.progress || 0)

          fetchTimer = setTimeout(() => {
            generateSentiments({
              job: res,
              codebook,
              onFetch,
              onError,
              onProgress,
              setIsLoading,
              onFetchApiKey
            })
          }, 5000)
        } else errorCallback()
        break
      }
      case 'INVALID KEY': {
        if (onFetchApiKey && 'function' == typeof onFetchApiKey) {
          onFetchApiKey(() => {
            generateSentiments({
              job,
              codebook,
              onFetch,
              onError,
              onProgress,
              setIsLoading,
              onFetchApiKey
            })
          })
        } else errorCallback()
        break
      }
      case 'FAIL':
      default: {
        errorCallback(res.error)
        break
      }
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log(error)
    errorCallback()
  }
}

export const generateSurveySkipper = async ({
  job,
  codebook,
  selectedQuestion = '*',
  guided = false,
  onFetch,
  onError = () => {},
  onProgress = () => {},
  setIsLoading = () => {},
  onFetchApiKey
}) => {
  let fetchTimer = null

  const errorCallback = message => {
    setIsLoading(false)

    fetchTimer && clearTimeout(fetchTimer)

    onError(
      Array.isArray(message) && 0 < message.length
        ? message
        : ['Sorry, mate, I&apos;ve lost my bearings...', 'Let&apos;s give it another go in a bit.']
    )
  }

  try {
    const abortController = new AbortController()

    setIsLoading(true)

    const { signal } = abortController
    const res = await runSurveySkipper({ job, codebook, selectedQuestion, guided, signal })

    switch (res.status) {
      case 'OK':
      case 'NOT FOUND': {
        fetchTimer && clearTimeout(fetchTimer)

        const message = res.result?.data?.message || []

        if (Object.hasOwn(res.result?.data || {}, 'survey') || Object.hasOwn(res || {}, 'survey')) {
          onProgress(100)

          if (onFetch && 'function' == typeof onFetch) {
            onFetch(res.result?.data || res, () => setIsLoading(false, message))
          }
        } else errorCallback(message)
        break
      }
      case 'RUNNING': {
        if (res && Object.hasOwn(res, 'token')) {
          onProgress(res.progress || 0)

          fetchTimer = setTimeout(() => {
            generateSurveySkipper({
              job: res,
              codebook,
              selectedQuestion,
              guided,
              onFetch,
              onError,
              onProgress,
              setIsLoading,
              onFetchApiKey
            })
          }, 5000)
        } else errorCallback()
        break
      }
      case 'INVALID KEY': {
        if (onFetchApiKey && 'function' == typeof onFetchApiKey) {
          onFetchApiKey(() => {
            generateSurveySkipper({
              job,
              codebook,
              selectedQuestion,
              guided,
              onFetch,
              onError,
              onProgress,
              setIsLoading,
              onFetchApiKey
            })
          })
        } else errorCallback()
        break
      }
      case 'FAIL':
      default: {
        errorCallback(res.error)
        break
      }
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log(error)
    errorCallback()
  }
}

export const generateSurveyTranslation = async ({
  job,
  translationLanguage,
  onFetch,
  onError = () => {},
  onProgress = () => {},
  setIsLoading = () => {},
  onFetchApiKey
}) => {
  let fetchTimer = null

  const errorCallback = message => {
    setIsLoading(false)

    fetchTimer && clearTimeout(fetchTimer)

    onError(
      Array.isArray(message) && 0 < message.length
        ? message
        : ['Lost my bearing...', 'Let&apos;s give it another go in a bit.']
    )
  }

  try {
    const abortController = new AbortController()

    setIsLoading(true)

    // setTimeout(() => {
    //   onProgress(100)
    //   onFetch(() => setIsLoading(false, 'nice work'))
    // }, 2000)

    // return

    const { signal } = abortController
    const res = await runSurveyTranslation({ job, translationLanguage, signal })

    switch (res.status) {
      case 'OK':
      case 'NOT FOUND': {
        fetchTimer && clearTimeout(fetchTimer)

        const message = res.result?.data?.message || []

        if ('completed' == res.result?.state) {
          onProgress(100)

          if (onFetch && 'function' == typeof onFetch) {
            onFetch(() => setIsLoading(false, message))
          }
        } else errorCallback(message)
        break
      }
      case 'RUNNING': {
        if (res && Object.hasOwn(res, 'token')) {
          onProgress(res.progress || 0)

          fetchTimer = setTimeout(() => {
            generateSurveyTranslation({
              job: res,
              translationLanguage,
              onFetch,
              onError,
              onProgress,
              setIsLoading,
              onFetchApiKey
            })
          }, 5000)
        } else errorCallback()
        break
      }
      case 'INVALID KEY': {
        if (onFetchApiKey && 'function' == typeof onFetchApiKey) {
          onFetchApiKey(() => {
            generateSurveyTranslation({
              job,
              translationLanguage,
              onFetch,
              onError,
              onProgress,
              setIsLoading,
              onFetchApiKey
            })
          })
        } else errorCallback()
        break
      }
      case 'FAIL':
      default: {
        errorCallback(res.error)
        break
      }
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log(error)
    errorCallback()
  }
}

export default {}
